import { Injectable } from '@angular/core';
import { EnumLang } from '@sc/data-access-models';
import {
  EnumJsonListKey,
  EnumJsonObjectKey,
  EnumLangKey,
  EnumStringKey,
  isKeyType,
  MapKeys,
  StorageKey,
} from '../models/storage-key';

@Injectable({ providedIn: 'root' })
export class StorageService {
  private static readonly noSetKeys: StorageKey[] = ['okta-token-storage'];

  getItem<T extends StorageKey, U = any>(
    key: T,
    storage: Storage = localStorage,
    defaultValue?: MapKeys<T, U>
  ): MapKeys<T, U> {
    const value: string | null = storage.getItem(key);
    if (this.isValidValue(key, value)) {
      return this.parse<T, U>(key, value as string);
    }
    return this.getDefaultValue<T, U>(key, defaultValue);
  }

  setItem<T extends StorageKey, U = any>(
    key: T,
    value: MapKeys<T, U>,
    storage: Storage = localStorage,
    defaultValue?: MapKeys<T, U>
  ): void {
    if (StorageService.noSetKeys.includes(key)) {
      return;
    }

    let valueStringified: string;
    valueStringified = JSON.stringify(value);
    if (!this.isValidValue(key, valueStringified)) {
      valueStringified = JSON.stringify(
        this.getDefaultValue<T, U>(key, defaultValue)
      );
    }
    storage.setItem(key, valueStringified);
  }

  removeItem<T extends StorageKey>(key: T, storage: Storage = localStorage) {
    storage.removeItem(key);
  }

  setDisplayProductTourOn(url: string, value: boolean) {
    const key = 'displayProductTourOn' + url;
    localStorage.setItem(key, JSON.stringify(value));
  }

  getDisplayProductTourOn(url: string): boolean {
    const value = JSON.parse(
      localStorage.getItem('displayProductTourOn' + url) || 'true'
    );
    if (typeof value === 'boolean') {
      return value;
    }
    return true;
  }

  getDefaultValue<T extends StorageKey, U = any>(
    key: T,
    defaultValue?: MapKeys<T, U>
  ): MapKeys<T, U> {
    if (defaultValue !== undefined) {
      return defaultValue as MapKeys<T, U>;
    }
    return null as unknown as MapKeys<T>;
  }

  isValidValue<T extends StorageKey>(
    key: T,
    valueString: string | null
  ): boolean {
    if (
      valueString === undefined ||
      valueString === 'undefined' ||
      valueString === null ||
      valueString === 'null'
    ) {
      return false;
    }
    if (
      isKeyType(key, EnumJsonObjectKey) &&
      (!valueString.startsWith('{') || !valueString.endsWith('}'))
    ) {
      return false;
    }
    if (
      isKeyType(key, EnumJsonListKey) &&
      (!valueString.startsWith('[') || !valueString.endsWith(']'))
    ) {
      return false;
    }
    try {
      if (
        isKeyType(key, EnumLangKey) &&
        !Object.values(EnumLang).includes(JSON.parse(valueString) as any)
      ) {
        return false;
      }
      // if (
      //   isKeyType(key, EnumDateKey) &&
      //   !dayjs(JSON.parse(valueString)).isValid()
      // ) {
      //   return false;
      // }
    } catch (err) {
      // remove the try catch in February 2023
      return false;
    }
    return true;
  }

  parse<T extends StorageKey, U = any>(
    key: T,
    valueString: string
  ): MapKeys<T, U> {
    let valueParsed: MapKeys<T, U>;
    try {
      //remove try catch in February 2023
      valueParsed = JSON.parse(valueString);
    } catch (err) {
      if (
        // isKeyType(key, EnumDateKey) ||
        isKeyType(key, EnumStringKey)
      ) {
        valueParsed = valueString as MapKeys<T, U>;
      } else {
        return null as MapKeys<T, U>;
      }
    }
    // if (isKeyType(key, EnumDateKey)) {
    //   return dayjs(valueParsed as string).toDate() as MapKeys<T>;
    // }
    return valueParsed;
  }
}
